import { CircleInfoIcon } from "@xola/icons";
import { Alert, Switch } from "@xola/ui-kit";
import clsx from "clsx";
import {
    DateFormat,
    formatDate,
    getExperienceMin,
    hasTimeScheduled,
    isEquipmentBased,
    isPackageProduct,
    isPartnerPackageProduct,
    isSame,
    isToday,
    now,
} from "common";
import dayjs, { Dayjs, isDayjs } from "dayjs";
import { find, isEmpty, isEqual, isNil, isNumber } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Arrival, ScheduleType, Timeslot } from "types";
import { useDebouncedCallback } from "use-debounce";
import { useSeller, useUser } from "../../hooks/useAccount";
import { useModal } from "../../hooks/useVisibility";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { ManageActionButton } from "../Dashboard/Roster/ManageActionButton";
import { ArrivalDatePicker } from "../DatePicker/ArrivalDatePicker";
import {
    getBookableDates,
    getBookableSlots,
    getFirstAvailableTimeslot,
    getNextBookableDate,
    getUnavailabilityMessagesByDate,
    getWaitlistDates,
    TimeslotFilterOptions,
} from "../DatePicker/ArrivalPickerHelper";
import { ArrivalPickerSkeleton } from "../DatePicker/ArrivalPickerSkeleton";
import { ArrivalTimePicker } from "../DatePicker/ArrivalTimePicker";
import { OverrideFutureCutoffSwitch } from "../DatePicker/OverrideFutureCutoffSwitch";
import { PastAvailabilitySwitch } from "../DatePicker/PastAvailabilitySwitch";
import { useLastScheduledDate, useUpcomingDates } from "../DatePicker/useUpcomingDates";
import { CreateExperienceTimeslotModal } from "../PurchaseModification/CreateExperienceTimeslotModal";
import { ArrivalRestrictions } from "./ArrivalRestrictions";
import { useArrivalTimeslots } from "./hooks/useArrivalTimeslots";
import { isTimeslotInProgress } from "@/models/Timeslot";
import { isAffiliate } from "@/models/User";
import { allowPastDates, isWaitlistEnabled } from "@/stores/globalStore";

interface ArrivalPickerProps {
    arrivalDate: string;
    arrivalTime: number | null;
    onChange: (date: string, time: number | null, slot?: Timeslot) => void;
    timezone?: string;
    itemIndex?: number;
}

export const ArrivalPicker = React.memo(
    ({ arrivalDate, arrivalTime, onChange, timezone, itemIndex }: ArrivalPickerProps) => {
        const user = useUser();
        const seller = useSeller();
        const placement = window.innerWidth < 1090 ? "bottom" : "right";
        const itemIndexFromStore = useCreatePurchaseStore((state) => state.activeItemIndex);
        const activeItemIndex = itemIndex ?? itemIndexFromStore;
        const experience = useCreatePurchaseStore((state) => state.getItemExperienceByIndex(activeItemIndex));
        const timeslot = useCreatePurchaseStore((state) => state.getActiveItem().timeslot);

        const privacy = useCreatePurchaseStore((state) => state.getActiveItem().privacy);

        const quantity = getExperienceMin(experience);
        const product = experience.id;
        const timeslotFilterOptions: TimeslotFilterOptions = useMemo(
            () => ({
                cutoffType: experience.cutoffType ?? null,
                cutoffDuration: experience.cutoff,
            }),
            [experience.cutoffType, experience.cutoff],
        );
        const isWaitlistAllowed = experience.waitlist?.enabledForBackOffice && isWaitlistEnabled();
        const shouldShowshowAllTimeslotsSwitch = isPackageProduct(experience) ? false : isWaitlistAllowed;
        const showTimeslotsSwitch = shouldShowshowAllTimeslotsSwitch;
        const [isPastAvailabilityChecked, setIsPastAvailabilityChecked] = useState(false);
        const [isOverrideFutureCutoffChecked, setOverrideFutureCutoffChecked] = useState(false);
        const datePickerPreference = seller.preferences.datepickerPreference;
        const showAllTimeslotsSwitch =
            Boolean(showTimeslotsSwitch) || Boolean(datePickerPreference.showUnavailableTimes);
        const [month, setMonth] = useState(now(arrivalDate)); // The month on which the date picker is visible now -- different from selected date

        const {
            data: timeslots,
            isLoading,
            hasNextPage,
            isFetching,
            isFetchingNextPage,
        } = useArrivalTimeslots({
            product,
            quantity,
            start: month,
            showAllTimeslots: showAllTimeslotsSwitch,
            isPastAvailabilityChecked,
            isOverrideFutureCutoffChecked,
            privacy: privacy,
            timezone,
            filterOptions: timeslotFilterOptions,
            isEquipmentBased: isEquipmentBased(experience),
        });

        const isOngoingRequest = isFetching ?? isLoading ?? hasNextPage ?? isFetchingNextPage;

        const showAddNewTimeSlot = hasTimeScheduled(experience);
        const allowPastDate = allowPastDates();
        const allowAllTimeslots = showAllTimeslotsSwitch || allowPastDate;
        const allowWaitlist = shouldShowshowAllTimeslotsSwitch && !isPackageProduct(experience);
        const isMist = useCreatePurchaseStore((state) => state.isMist());

        const upcomingStartDate = formatDate(month.startOf("month")); // Set the start date to the start of the month so we can fetch upcoming dates

        const [selectedTimeslots, setSelectedTimeslots] = useState<Timeslot[]>([]);
        const [localTimezone, setLocalTimezone] = useState(timezone);

        useEffect(() => {
            if (arrivalDate && !isSame(month, arrivalDate, "month")) {
                // When the incoming date to this component changes, update the month so that the timeslots API call
                // uses the correct start month
                setMonth(now(arrivalDate));
            }

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [arrivalDate]);

        const createTimeslotModal = useModal(false);

        const isWaitlistedAllowed = isWaitlistEnabled() && allowWaitlist;

        // This is times (arrivalTime) only, not for the entire slot
        // By default we show all times only when show waitlist is enabled
        const [showAllTimes, setShowAllTimes] = useState(isWaitlistedAllowed ?? false);
        const handleShowAllTimesChange = (value: boolean) => {
            setShowAllTimes(value);
            setSelectedTimeslots([]);
        };

        const handlePastAvailabilityChange = () => setIsPastAvailabilityChecked(!isPastAvailabilityChecked);
        const handleOverrideFutureCutoffChange = () => setOverrideFutureCutoffChecked(!isOverrideFutureCutoffChecked);

        const isUserAffiliate = isAffiliate(user);

        const { data: lastScheduledDate } = useLastScheduledDate(product, datePickerPreference);

        const productData = experience;

        const productTimeslots = useMemo(() => timeslots, [timeslots]);

        const next30Days = useMemo(() => now(upcomingStartDate).add(30, "days"), [upcomingStartDate]);

        const { upcomingDates, shouldFetchUpcoming } = useUpcomingDates(
            productTimeslots,
            quantity,
            upcomingStartDate,
            product,
            isOngoingRequest,
        );

        const handleMonthChange = (date: Date | Dayjs) => {
            // TODO: To be fixed in ui-kit
            // Temporary fix for the issue of DatePicker from ui-kit, when selecting any date, the onMonthChange callback is getting called.
            const isFirstDateOfMonth = dayjs(date).date() === 1;
            if (!isFirstDateOfMonth) {
                return;
            }

            const selectedMonth = isDayjs(date) ? date : now(date);
            const currentDate = now();
            const selectedDate =
                !isPastAvailabilityChecked && selectedMonth.isBefore(currentDate) ? currentDate : selectedMonth;
            setMonth(selectedDate);
        };

        /**
         * True until the privacy effect has handled a privacy switch. Timeslots of the day are preserved across
         * calendar month switches, but must be recalculated after a privacy switch.
         */
        const previousPrivacyRef = useRef(privacy);
        const isPrivacyChanged = previousPrivacyRef.current !== privacy;

        const debouncedOnMonthChange = useDebouncedCallback(handleMonthChange, 200, { leading: false, trailing: true });

        const { bookableDates, waitlistDates, pastDates } = useMemo(() => {
            const [bookableDates, pastDates] = getBookableDates({
                timeslots: productTimeslots,
                product: product,
                allowPastDates: allowPastDate,
                showAllTimeslots: showAllTimes,
                allowOverlap: false,
                isMist,
            });

            const waitlistDates = isWaitlistedAllowed
                ? getWaitlistDates(productTimeslots, bookableDates).filter((date) => !!bookableDates[formatDate(date)])
                : [];

            return { bookableDates, waitlistDates, pastDates };
        }, [productTimeslots, product, allowPastDate, showAllTimes, isMist, isWaitlistedAllowed]);

        const unavailabilityMessagesByDate = useMemo(
            () => getUnavailabilityMessagesByDate(productTimeslots, product),
            [productTimeslots, product],
        );

        const allBookableDates = useMemo(() => {
            return bookableDates[arrivalDate] ?? pastDates[arrivalDate] ?? null;
        }, [bookableDates, pastDates, arrivalDate]);

        // This is use for showing the time picker and has too many dependencies
        const timeslotsOfDay = useCallback(() => {
            if (!isPrivacyChanged) {
                const isSelectedTimeslotPresent = find(selectedTimeslots, { date: arrivalDate, time: arrivalTime });
                const isArrivalTimeSet = isNumber(arrivalTime);

                if (isArrivalTimeSet && isSelectedTimeslotPresent && localTimezone === timezone) {
                    return selectedTimeslots;
                }
            }

            const result = allBookableDates?.filter((timeslot) => {
                const hasOpenSlots = timeslot.capacity.open >= quantity;
                const hasTimeslotsWithoutOpenSlots = allowAllTimeslots && !hasOpenSlots;
                const hideMaxZeroSlots = timeslot.capacity.max === 0 && !allowAllTimeslots;
                if (hideMaxZeroSlots) {
                    return false;
                }

                const isAvailable = hasOpenSlots || hasTimeslotsWithoutOpenSlots;

                return isMist ? hasOpenSlots : isAvailable;
            });
            return result;
        }, [
            allBookableDates,
            arrivalDate,
            arrivalTime,
            allowAllTimeslots,
            quantity,
            isMist,
            selectedTimeslots,
            localTimezone,
            timezone,
            isPrivacyChanged,
        ]);

        const handleValueChange = useCallback(
            (newArrivalDate: string, newArrivalTime: number | null, slot?: Timeslot, clearCurrentDateSlots = false) => {
                const hasSlot = slot ? selectedTimeslots.includes(slot) : true;

                if (newArrivalDate !== arrivalDate || newArrivalTime !== arrivalTime || !hasSlot) {
                    onChange(newArrivalDate, newArrivalTime, slot);

                    setSelectedTimeslots(clearCurrentDateSlots ? [] : timeslotsOfDay() ?? []);
                }
            },
            [onChange, timeslotsOfDay, arrivalDate, arrivalTime, selectedTimeslots],
        );

        const nextUpcomingDate = upcomingDates && upcomingDates.length > 0 ? upcomingDates[0] : null;

        // We don't need to wait for all API calls to be made to set a slot automatically if it's not set currently.
        // Since we can use data from the 1st API call.
        const skipEffect = isOngoingRequest && !isFetchingNextPage;

        /**
         * Checks if privacy for the selected timeslot is correct according to the privacy currently selected.
         * if both privacies are allowed we return true, since we don't need to compare.
         * If privacyPref is not passed to the component we will skip this check.
         * If `timeslot.privacy` is not defined this means timeslot available for both `private` and `public`.
         */
        const isPrivacyNotDefined = isNil(privacy) || isNil(timeslot?.privacy);
        const isSamePrivacySlotSelected = timeslot?.privacy === privacy;
        const isSamePrivacyTimeslot = isPrivacyNotDefined || isSamePrivacySlotSelected;

        useEffect(() => {
            const bookableSlots = getBookableSlots(arrivalDate, bookableDates);

            // If the purchase has a CONFIRMED status, we don't need to reset the date again (except when there is only one slot).
            if (skipEffect) {
                return;
            }
            // If the purchase has a CONFIRMED status, we don't need to reset the date again (except when there is only one slot).

            /** First bookable timeslot after seller's current time. */

            const nextBookableTimeslot = getFirstAvailableTimeslot(
                bookableSlots,
                isPastAvailabilityChecked,
                arrivalDate,
                timeslotFilterOptions,
            );

            if (bookableSlots.length > 0 && nextBookableTimeslot) {
                const { date, time } = nextBookableTimeslot;

                /**
                 * Checks if we should auto-select timeslot instead currently selected.
                 * If `timeslot.product` is not the same as product passed, the package/mist booking case, when we can have slot from another experience.
                 * If `timeslot.privacy` is not the same as privacy passed, since timeslot is timeslot public only we need to reselect from available ones and vice versa.
                 * If `arrivalTime` or selected timeslot `time` is set, but current value missing time, we need to auto select first available (switch experience, upcoming date cases).
                 */
                const isValueMissingTime = (!isNil(arrivalTime) || !isNil(timeslot?.time)) && isNil(arrivalTime);
                // A privacy switch is handled by the privacy effect below, which applies capacity and cutoff checks
                const hasPrivacyMismatch = !isSamePrivacyTimeslot && !isPrivacyChanged;
                const shouldSetTimeslot = timeslot?.product !== product || hasPrivacyMismatch || isValueMissingTime;
                if (shouldSetTimeslot && !arrivalTime) {
                    // Handles case when both timeslot and arrivalTime are not set.
                    handleValueChange(date, time, nextBookableTimeslot);
                } else if (shouldSetTimeslot && arrivalTime) {
                    // Handles case when user redirected from the dashboard with predefined time,
                    // if initial timeslot is not available select the next bookable.
                    const selectedSlot = find(bookableSlots, { time: arrivalTime, date: arrivalDate });
                    const itemArrivalTime = selectedSlot ? arrivalTime : time;
                    const timeslot = selectedSlot ? selectedSlot : nextBookableTimeslot;
                    handleValueChange(date, itemArrivalTime, timeslot);
                }
            } else if (!timeslot?.canBook && !isEmpty(bookableDates)) {
                // The selected bookable date is not available. Select the next one after arrivalDate
                const nextBookableDate = getNextBookableDate(arrivalDate, bookableDates);
                if (nextBookableDate) {
                    const { date, time, timeslot } = nextBookableDate;
                    handleValueChange(date, time, timeslot);
                }
            } else if (
                nextUpcomingDate &&
                bookableSlots.length === 0 &&
                nextUpcomingDate !== month.format("YYYY-MM-DD")
            ) {
                setMonth(now(nextUpcomingDate));
            }

            // Don't add timeslot as a dependency as it conflicting if we have multiple Products in Package (X2-9730)
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            skipEffect,
            bookableDates,
            nextUpcomingDate,
            arrivalDate,
            arrivalTime,
            product,
            handleValueChange,
            isPastAvailabilityChecked,
            isOverrideFutureCutoffChecked,
            timeslotFilterOptions,
        ]);

        const handleDayChange = (value: Date, options: { disabled: boolean }) => {
            if (options && !options.disabled) {
                const date = formatDate(value);

                // `getBookableSlots` can return a slot for another date,
                // if the value passed has the date in the future (2 or more months in advance),
                // so we need to explicitly filter for passed date slots to avoid the case when we select the date,
                // and the time which is not available for selected date.
                const slots = getBookableSlots(date, bookableDates).filter((slot) => slot.date === date);

                const availableSlot =
                    slots.find((slot) => slot.capacity.open >= quantity) ?? allowWaitlist ? slots[0] : undefined;

                // The date is changed, so we need to unselect the timeslot.
                // No timezone change required because we have already set the default timezone when the user logged in
                const { time } = slots.length > 0 && availableSlot ? availableSlot : { time: null }; // set time to bookable timeslot
                handleValueChange(date, time, availableSlot, true);
            }
        };

        const handleTimeslotChange = (time: number) => {
            const slot = getBookableSlots(arrivalDate, bookableDates).find((slot) => slot.time === time);
            handleValueChange(arrivalDate, time, slot);
        };

        const currentDateTimeslots = timeslotsOfDay()?.filter((timeslot: Timeslot) => timeslot.time !== null) ?? [];
        const hasTimeslot = currentDateTimeslots.length > 0;
        const shouldUpdateSelectedDateTimeslots = isNumber(arrivalTime) && hasTimeslot;

        useEffect(() => {
            if (isPrivacyChanged && !isOngoingRequest) {
                setSelectedTimeslots([]);
                previousPrivacyRef.current = privacy;

                const bookableSlots = getBookableSlots(arrivalDate, bookableDates);
                const nextBookableTimeslot = getFirstAvailableTimeslot(
                    bookableSlots,
                    isPastAvailabilityChecked,
                    arrivalDate,
                    timeslotFilterOptions,
                );
                const selectedSlots = bookableSlots.filter(
                    (slot) => slot.date === arrivalDate && slot.time === arrivalTime,
                );
                const currentTimeslot = getFirstAvailableTimeslot(
                    selectedSlots,
                    isPastAvailabilityChecked,
                    arrivalDate,
                    timeslotFilterOptions,
                );

                const hasRoomForParty = currentTimeslot?.canBook && currentTimeslot.capacity.open >= quantity;
                // Below the party size a canBook slot is waitlist-only, so keep it only when waitlisting is allowed
                const canKeepCurrentTimeslot = !isNil(currentTimeslot) && (allowWaitlist || hasRoomForParty);
                // Re-selecting a kept slot still matters: the item takes the slot (capacity, privacy) fetched for the new privacy
                const slotToSelect = canKeepCurrentTimeslot ? currentTimeslot : nextBookableTimeslot;
                if (slotToSelect) {
                    handleValueChange(slotToSelect.date, slotToSelect.time, slotToSelect);
                }
            } else if (shouldUpdateSelectedDateTimeslots) {
                // If timeslots have changed, update the state. But we should do it carefully to not get into an
                // infinite loop of re-renders
                if (selectedTimeslots.length !== currentDateTimeslots.length) {
                    setSelectedTimeslots(currentDateTimeslots);
                } else if (!isEqual(currentDateTimeslots, selectedTimeslots)) {
                    // Only execute isEqual if we really need it
                    setSelectedTimeslots(currentDateTimeslots);
                }

                if (timezone !== localTimezone) {
                    setLocalTimezone(timezone);
                }
            }

            /**
             * The only triggers should be `productTimeslots` and `shouldUpdateSelectedDateTimeslots`.
             * Not adding `currentDateTimeslots` to avoid possible loop between this and `timeslotsOfDay`.
             * Not adding `isPrivacyChanged` to avoid non-needed re-renders since we already re-fetch timeslots on privacy change.
             * Not adding `previousPrivacyRef.current` since it causes unexpected behavior, and is not required by the below lint rule.
             * Not adding `privacy` since it causes more re-renders,
             * and during the initial render cause an issue when slots with incorrect privacy might be displayed until you switch privacy again.
             */
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [productTimeslots, shouldUpdateSelectedDateTimeslots, isOngoingRequest]);

        if (isLoading) {
            return <ArrivalPickerSkeleton />;
        }

        const handleDisabledDays = (date: Date) => {
            const dt = formatDate(date);
            const cannotBookDate = !bookableDates[dt];
            if (isPastAvailabilityChecked) {
                return cannotBookDate;
            }

            if (isOngoingRequest) {
                return cannotBookDate;
            }

            const isPastDate = pastDates[dt];
            return cannotBookDate || Boolean(isPastDate);
        };

        const handleLoadingDays = (date: Date) => {
            const dt = formatDate(date);
            const cannotBookDate = !bookableDates[dt];

            if (isOngoingRequest) {
                return cannotBookDate;
            }

            return false;
        };

        const handleNewTimeslotDisabledDays = (date: Date) => {
            return isPastAvailabilityChecked ? false : now(date).isBefore(now(), "day");
        };

        const upcomingDatesList = upcomingDates
            ? upcomingDates
                  .filter((date) => now(date).isSameOrAfter(next30Days, "days"))
                  .map((date) => now(date).toDate())
            : [];

        const hasUpcomingSlots = upcomingDatesList.length > 0 || shouldFetchUpcoming;

        return (
            <>
                <ArrivalRestrictions />

                <div className="ui-arrival-date-time-picker">
                    <div className="space-y-1">
                        <span className="text-sm font-bold">Date</span>
                        <div className="flex w-full items-center space-x-6">
                            <ArrivalDatePicker
                                month={month}
                                placement={placement}
                                value={arrivalDate}
                                bookableDates={bookableDates}
                                unavailabilityMessagesByDate={unavailabilityMessagesByDate}
                                disabledDays={handleDisabledDays}
                                loadingDays={handleLoadingDays}
                                waitlistDates={isWaitlistedAllowed ? waitlistDates : undefined}
                                components={{
                                    Footer: () => (
                                        <div className="flex flex-row justify-between space-x-2">
                                            <PastAvailabilitySwitch
                                                isChecked={isPastAvailabilityChecked}
                                                onChange={handlePastAvailabilityChange}
                                            />
                                            <OverrideFutureCutoffSwitch
                                                productId={product}
                                                isChecked={isOverrideFutureCutoffChecked}
                                                onChange={handleOverrideFutureCutoffChange}
                                            />
                                        </div>
                                    ),
                                }}
                                upcomingDates={hasUpcomingSlots ? upcomingDatesList : undefined}
                                datePickerPreference={datePickerPreference}
                                lastScheduledDate={lastScheduledDate}
                                onChange={handleDayChange}
                                onMonthChange={debouncedOnMonthChange}
                            />
                        </div>
                    </div>
                    {hasTimeslot && (
                        <ArrivalTimePicker
                            value={arrivalTime}
                            timeslots={timeslotsOfDay()}
                            allowWaitlist={allowWaitlist}
                            onChange={handleTimeslotChange}
                        />
                    )}
                    <TimeslotInProgressAlert />
                    <div className={clsx("mt-4", showAllTimeslotsSwitch && "flex justify-between")}>
                        {showAddNewTimeSlot && !isUserAffiliate && (
                            <ManageActionButton
                                text="Add a new time"
                                disabled={isPartnerPackageProduct(productData) || productData?.seller.id !== seller.id}
                                onClick={createTimeslotModal.show}
                            />
                        )}
                        {showAllTimeslotsSwitch && (
                            <Switch.Group>
                                <Switch size="medium" isChecked={showAllTimes} onChange={handleShowAllTimesChange} />
                                <Switch.Label direction="right">Show all times</Switch.Label>
                            </Switch.Group>
                        )}
                    </div>

                    {createTimeslotModal.isVisible && (
                        <CreateExperienceTimeslotModal
                            showCapacity
                            timeslot={timeslots?.[0]}
                            isOpen={createTimeslotModal.isVisible}
                            experienceId={product}
                            initialValue={now(arrivalDate)}
                            showAllTimes={showAllTimes}
                            disabledDays={handleNewTimeslotDisabledDays}
                            onClose={(timeslot: Timeslot, date?: Dayjs) => {
                                if (date && isDayjs(date)) {
                                    const newTime = Number(date.format("Hmm"));
                                    const payload: Arrival = { date: formatDate(date), time: null };

                                    // TODO: We need to check if the time is actually created since `0` also means midnight
                                    if (newTime >= 0) {
                                        payload.time = newTime;
                                    }

                                    handleValueChange(payload.date, payload.time, timeslot, true);
                                }

                                createTimeslotModal.hide();
                            }}
                        />
                    )}
                </div>
            </>
        );
    },
);

ArrivalPicker.displayName = "ArrivalPicker";

const TimeslotInProgressAlert = React.memo(() => {
    const timeslot = useCreatePurchaseStore((state) => state.getActiveItem().timeslot);
    const timeslotEnd = now(timeslot?.end).add(1, "second").startOf("minute");
    const timeslotEndFormatted = formatDate(
        timeslotEnd,
        isToday(timeslotEnd) ? DateFormat.TIME : DateFormat.DATE_TIME_SHORT,
    );

    if (timeslot?.scheduleType === ScheduleType.TIME && isTimeslotInProgress(timeslot)) {
        return (
            <div className="mt-4">
                <Alert color="primary">
                    <CircleInfoIcon size="medium" /> This event is currently in progress and ends at{" "}
                    <span>{timeslotEndFormatted}</span>
                </Alert>
            </div>
        );
    }

    return null;
});

TimeslotInProgressAlert.displayName = "TimeslotInProgressAlert";
